import React, { useEffect, useRef, useState } from 'react';
import {FormattedMessage} from "react-intl";
import { connect } from 'react-redux';
import { closeModalFix, nextStepModalFix, prevStepModalFix, skipStepModalFix } from "../../../../../actions/sqftModalFunctions";
import {useFadeSlide} from "../../../hocs/useFadeSlide";
import {useDraggableClamp} from "../../../hocs/useDraggable";

const ModalFix = (props) => {
  const {
    sqftModalFixState,
    closeModalFix,
    nextStepModalFix,
    prevStepModalFix,
    skipStepModalFix
  } = props;

  const {
    isModalFixOpen,
    currentStep,
    totalSteps,
    title,
    content,
    showSkip,
    showPrev,
    showNext,
    showClose,
    showCancel,
    nextButtonText,
    skipButtonText,
    steps,
    enterAnim = 'fadeDown',
    exitAnim = 'fadeUp',
    animDurationMs = 220,
    animOffsetPx = 24,
    dragEnabled = true,
    clampMargin = 12,
    onComplete,
    isNextButtonDisabled,
  } = sqftModalFixState;

  const [forceExit, setForceExit] = useState(false);
  const timeoutRef = useRef(null);

  const drag = useDraggableClamp({
    enabled: dragEnabled,
    clampMargin,
    initial: { x: 0, y: 0 }
  });

  const anim = useFadeSlide({
    visible: isModalFixOpen && !forceExit,
    enter: enterAnim,
    exit: exitAnim,
    duration: animDurationMs,
    offset: animOffsetPx,
  });

  const surfaceRef = anim.nodeRef;
  const [lockSize, setLockSize] = useState(null);

  const frozen = useRef({
    title,
    content,
    step: { currentStep, totalSteps },
    controls: { showSkip, showPrev, showNext, showClose, showCancel, nextButtonText, skipButtonText }
  });

  // Update frozen content when not in force exit state
  useEffect(() => {
    if (!forceExit) {
      frozen.current = {
        title,
        content,
        step: { currentStep, totalSteps },
        controls: { showSkip, showPrev, showNext, showClose, showCancel, nextButtonText, skipButtonText }
      };
    }
  }, [forceExit, title, content, currentStep, totalSteps, showSkip, showPrev, showNext, showClose, showCancel, nextButtonText, skipButtonText]);

  // Reset states when modal closes
  useEffect(() => {
    if (!isModalFixOpen) {
      setForceExit(false);
      setLockSize(null);
      drag.reset(0, 0);
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    }
  }, [isModalFixOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle close button click
  const handleCloseClick = () => {
    if (surfaceRef.current) {
      const r = surfaceRef.current.getBoundingClientRect();
      setLockSize({ w: r.width, h: r.height });
    }
    setForceExit(true);
    // Mark that we want to restore on cancel
    drag.hostRef.current.dataset.shouldRestore = 'true';
  };

  // Handle next button click
  const onClickNext = () => {
    if (forceExit || isNextButtonDisabled) {
      return;
    }

    if (currentStep < totalSteps) {
      nextStepModalFix();
      return;
    }

    if (surfaceRef.current) {
      const r = surfaceRef.current.getBoundingClientRect();
      setLockSize({ w: r.width, h: r.height });
    }
    setForceExit(true);
    // Mark that we don't want to restore on finish
    drag.hostRef.current.dataset.shouldRestore = 'false';
  };

  // Handle force exit completion
  useEffect(() => {
    if (!forceExit) {
      return;
    }

    const el = surfaceRef.current;
    const hostEl = drag.hostRef.current;

    if (!el || !hostEl) {
      // Fallback: close immediately if no element
      setForceExit(false);
      setLockSize(null);
      closeModalFix(true); // Default to restore on unexpected close
      if (typeof onComplete === 'function') {
        onComplete();
      }
      return;
    }

    // Check if we should restore snapshot
    const shouldRestore = hostEl.dataset.shouldRestore !== 'false';

    const onTransitionEnd = (e) => {
      // Only handle transitions on the main surface element
      if (e.target !== el) return;

      setForceExit(false);
      setLockSize(null);
      closeModalFix(shouldRestore);

      // Only call onComplete when finishing (not canceling)
      if (!shouldRestore && typeof onComplete === 'function') {
        onComplete();
      }
    };

    // Add transition end listener
    el.addEventListener('transitionend', onTransitionEnd, { once: true });

    // Fallback timeout in case transitionend doesn't fire
    timeoutRef.current = setTimeout(() => {
      el.removeEventListener('transitionend', onTransitionEnd);
      setForceExit(false);
      setLockSize(null);
      closeModalFix(shouldRestore);

      // Only call onComplete when finishing (not canceling)
      if (!shouldRestore && typeof onComplete === 'function') {
        onComplete();
      }
    }, animDurationMs + 100);

    return () => {
      el.removeEventListener('transitionend', onTransitionEnd);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [onComplete, forceExit, closeModalFix, surfaceRef, drag.hostRef, animDurationMs]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  if (!anim.mounted) {
    return null;
  }

  if (!steps || !steps.length) {
    return null;
  }

  const renderTitle = forceExit ? frozen.current.title : title;
  const renderContent = forceExit ? frozen.current.content : content;
  const renderStep = forceExit ? frozen.current.step : { currentStep, totalSteps };
  const renderControls = forceExit ? frozen.current.controls : {
    showSkip, showPrev, showNext, showClose, showCancel, nextButtonText, skipButtonText
  };

  return (
    <div
      ref={drag.hostRef}
      className={`modal-fix modal-fix-outer ${drag.dragging ? 'modal-fix-outer--dragging' : ''}`}
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: `translate(calc(-50% + ${drag.pos.x}px), calc(-50% + ${drag.pos.y}px))`,
        transition: 'none',
        willChange: 'transform',
        zIndex: 10001,
        cursor: drag.dragging ? 'grabbing' : 'default',
        userSelect: drag.dragging ? 'none' : 'auto'
      }}
      {...drag.props}
    >
      <div
        ref={surfaceRef}
        className="modal-fix-surface"
        style={{
          ...anim.style,
          width: lockSize ? `${lockSize.w}px` : 'auto',
          height: lockSize ? `${lockSize.h}px` : 'auto',
          maxWidth: 640,
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
        }}
      >
        <div
          className="modal-fix-header"
          {...drag.headerDragProps}
          style={{
            cursor: dragEnabled ? 'grab' : 'default'
          }}
        >
          <div className="modal-fix-warning-icon">
            {/* Warning icon content */}
          </div>
          <h3 className="modal-fix-title">{renderTitle}</h3>
          {renderControls.showClose && (
            <button
              className="modal-fix-close-button"
              onPointerDown={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              onClick={handleCloseClick}
              aria-label="Close modal"
              disabled={forceExit}
            >
              ×
            </button>
          )}
        </div>

        <div className="modal-fix-body">{renderContent}</div>

        <div className="modal-fix-footer">
          <div className="modal-fix-step-indicator">
            Issue {renderStep.currentStep} of {renderStep.totalSteps}
          </div>
          <div className="modal-fix-buttons">
            {renderControls.showCancel && (
              <button
                className="ui-button3 ui-button3--blue modal-fix-button modal-fix-button--cancel"
                onClick={handleCloseClick}
                disabled={forceExit}
              >
                <FormattedMessage id={'Cancel'}/>
              </button>
            )}
            {renderControls.showPrev && (
              <button
                className="ui-button3 ui-button3--green-inverted modal-fix-button modal-fix-button--prev"
                onClick={prevStepModalFix}
                disabled={forceExit}
              >
                <FormattedMessage id={'Previous'}/>
              </button>
            )}
            {renderControls.showSkip && (
              <button
                className="ui-button3 modal-fix-button modal-fix-button--skip"
                onClick={skipStepModalFix}
                disabled={forceExit}
              >
                <FormattedMessage id={renderControls.skipButtonText}/>
              </button>
            )}
            {renderControls.showNext && (
              <button
                className={"ui-button3  modal-fix-button modal-fix-button--next " + (isNextButtonDisabled ? 'ui-button3--grey' : 'ui-button3--green')}
                onClick={onClickNext}
                disabled={forceExit}
              >
                <FormattedMessage id={renderControls.nextButtonText}/>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (store) => ({
  sqftModalFixState: store.sqftModalFixState
});

const mapDispatchToProps = {
  closeModalFix,
  nextStepModalFix,
  prevStepModalFix,
  skipStepModalFix
};

export default connect(mapStateToProps, mapDispatchToProps)(ModalFix);
